import { Cancel, Done } from '@mui/icons-material';
import { Button, Divider, FormControl, Grid, Radio, RadioGroup, Typography } from '@mui/material';
import { CreateTile, Icon } from '@thro-types/users';

import { Form, useFormikContext } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useIconsQuery } from 'api/hooks/useIconsQuery';
import StyledDropzone from 'app/components/Dropzone';
import { AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { TileDrawerActions } from 'app/containers/Dashboard/HomePage/tileDrawerSlice';
import { DrawerMode } from 'app/containers/Dashboard/HomePage/types';
import { translations } from 'locales/i18n';
import { ACLHelper } from 'utils/ACLHelper';
import { useAuthContext } from 'utils/hooks/useAuthContext';

import { uploadAttachmentToS3 } from '../helper';
import '../styles/DefaultIconsContent.scss';

export const DefaultIconsContent = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { values, setFieldValue } = useFormikContext<CreateTile>();
  const [previousValue, setPreviousValue] = useState<Icon | null>(values.icon);
  const [defaultIconsToUpload, setDefaultIconsToUpload] = useState<Blob | null>(null);
  const { user: currentUser } = useAuthContext();
  const aclHelper = new ACLHelper(currentUser);

  const {
    query: { data: icons, isLoading }
  } = useIconsQuery({ params: { isDefault: true } });

  const uploadDefaultIcon = () => {
    try {
      if (!defaultIconsToUpload) {
        return;
      }
      uploadAttachmentToS3({
        file: defaultIconsToUpload,
        icon: { ...values.icon, isDefault: true }
      });
      setDefaultIconsToUpload(null);
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.home.alerts.failedUpload())));
    }
  };

  return (
    <Form>
      <Grid
        container
        justifyContent={'space-between'}
        padding={2}
        flexDirection={'column'}
        minHeight={'100vh'}
      >
        <Grid container gap={4}>
          <Typography variant="h5" fontWeight={'bold'}>
            {t(translations.pages.home.cardDrawer.defaultIconDrawerTitle())}
          </Typography>
          <FormControl>
            <RadioGroup
              value={JSON.stringify(values.icon)}
              onChange={(event, value) => {
                const icon: Icon = JSON.parse(value); // ? This is because The DOM API casts this to a string.
                setFieldValue('icon', icon);
              }}
            >
              <Grid
                container
                maxHeight={'75vh'}
                overflow={'auto'}
                gap={2}
                justifyContent={'center'}
                alignItems={'center'}
              >
                {!isLoading && icons.length ? (
                  icons.map(icon => (
                    <div style={{ position: 'relative' }} key={icon.id}>
                      <img
                        src={`${import.meta.env.VITE_S3_IMAGE_PROXY}/${icon.path}`}
                        alt=""
                        style={{
                          maxWidth: 120,
                          minWidth: 120,
                          padding: '15px',
                          borderRadius: icon.isRounded ? '50%' : undefined
                        }}
                        className="iconToSelect"
                      />
                      <Radio className="icon__radio" value={JSON.stringify(icon)} />
                    </div>
                  ))
                ) : (
                  <>{t(translations.pages.home.cardDrawer.noDefaultIcons())}</>
                )}
              </Grid>
            </RadioGroup>
          </FormControl>
          {aclHelper.isSuperUser() && document.location.pathname === '/settings/tile-settings' && (
            <Grid container flexDirection={'column'} gap={1}>
              <Divider />
              <Grid item>
                <StyledDropzone
                  name="uploadDefaultIcon"
                  value={defaultIconsToUpload}
                  setFieldValue={(field, value) => setDefaultIconsToUpload(value)}
                  placeholder={t(translations.pages.home.cardDrawer.placeholder.uploadImage())}
                  className="product-create__dropzone"
                  accept={{
                    'image/jpeg': ['.jpeg', '.jpg'],
                    'image/png': ['.png'],
                    'image/svg+xml': ['.svg']
                  }}
                  single
                />
              </Grid>
              <Grid item>
                <Button
                  fullWidth
                  variant="contained"
                  disabled={defaultIconsToUpload === null}
                  onClick={() => uploadDefaultIcon()}
                >
                  {t(translations.common.upload())}
                </Button>
              </Grid>
            </Grid>
          )}
        </Grid>
        <Grid container justifyContent={'flex-end'} gap={2}>
          <Button
            variant="contained"
            style={{ color: 'white', backgroundColor: 'red' }}
            startIcon={<Cancel />}
            onClick={() => {
              setFieldValue('icon', previousValue);
              dispatch(TileDrawerActions.setCurrentMode(DrawerMode.CREATE));
            }}
          >
            {t(translations.common.cancel())}
          </Button>
          <Button
            variant="contained"
            style={{ color: 'white' }}
            startIcon={<Done />}
            onClick={() => {
              setPreviousValue(values.icon);
              dispatch(TileDrawerActions.setCurrentMode(DrawerMode.CREATE));
            }}
          >
            {t(translations.common.select())}
          </Button>
        </Grid>
      </Grid>
    </Form>
  );
};
